/**
 * Secțiuni verticale („curtains") prin volume atmosferice 3D.
 *
 * Principiu: pentru o felie A→B eșantionăm volumul pe traseu (interpolare
 * biliniară în plan, liniară în altitudine), construim o textură în care
 * rândurile sunt echidistante în altitudine, și o întindem pe un `wall`
 * Cesium. Peretele e desenat cu exagerare verticală, altfel 5 km de
 * atmosferă peste 600 km de teren sunt invizibili.
 *
 * Feliile pot fi mutate interactiv. Ca tragerea să rămână fluidă, randarea
 * are două regimuri: unul rapid, cu textură mai mică și profil de teren luat
 * din cache, și unul complet, la eliberarea mouse-ului.
 *
 * Modulul e agnostic față de conținut: primește surse Zarr și o `colorFn`,
 * deci poate felia deopotrivă poluanți CAMS și câmpuri meteo pe nivele de
 * presiune. Axa verticală a fiecărei surse e normalizată la metri.
 */
import {
  ArcType, Cartesian3, Cartographic, Color, ImageMaterialProperty,
  LabelStyle, VerticalOrigin, HorizontalOrigin, Cartesian2,
  NearFarScalar, Rectangle, sampleTerrainMostDetailed,
} from 'cesium';

import * as zarr from 'zarrita';

// Geometria peretelui e ieftină, deci o păstrăm densă în ambele regimuri.
const SAMPLES = 256;

// Rezoluția texturii: completă la comitere, redusă în timpul tragerii.
const TEX_COLS = 256;
const TEX_ROWS = 192;
const TEX_COLS_FAST = 128;
const TEX_ROWS_FAST = 96;

// Cuantizarea paletei. 1024 de trepte sunt sub pragul vizibil, dar scutesc
// ~50.000 de apeluri de colorFn pe felie.
const LUT_SIZE = 1024;

/** Interpolare biliniară într-un nivel al cubului (grilă lat/lon crescătoare). */
function bilinear(data, nlat, nlon, levOffset, fy, fx) {
  const y0 = Math.max(0, Math.min(nlat - 1, Math.floor(fy)));
  const x0 = Math.max(0, Math.min(nlon - 1, Math.floor(fx)));
  const y1 = Math.min(nlat - 1, y0 + 1);
  const x1 = Math.min(nlon - 1, x0 + 1);
  const ty = fy - y0;
  const tx = fx - x0;

  const v00 = data[levOffset + y0 * nlon + x0];
  const v01 = data[levOffset + y0 * nlon + x1];
  const v10 = data[levOffset + y1 * nlon + x0];
  const v11 = data[levOffset + y1 * nlon + x1];

  const a = v00 + (v01 - v00) * tx;
  const b = v10 + (v11 - v10) * tx;
  return a + (b - a) * ty;
}

export class VolumeSlicer {
  /**
   * @param {import('cesium').Viewer} viewer
   * @param {object} opts
   * @param {string} opts.url        URL-ul store-ului Zarr 3D
   * @param {(value:number)=>number[]} opts.colorFn  valoare → [r,g,b,a]
   */
  constructor(viewer) {
    this.viewer = viewer;

    this.available = false;
    this.sourceKey = null;   // sursa activă
    this.variable = null;    // câmpul activ din sursa activă
    this.colorFn = null;
    this.timeIndex = 0;
    this.exaggeration = 2;
    this.opacity = 1;        // multiplicator peste alfa dat de colorFn

    this._sources = new Map();  // cheie → { root, coords, altitudes, arrays, cache }
    this._curtains = [];     // definițiile feliilor, ca să le putem reconstrui
    this._entities = [];
    this._handle = null;     // punctul de la intersecția crucii
    this._cross = null;      // { lon, lat } când feliile sunt în cruce
    this._levelEntity = null;
    this._levelAlt = null;   // altitudinea feliei orizontale, null = ascunsă

    this._lut = null;
    this._lutKey = null;
    this._lutMin = 0;
    this._lutMax = 1;
  }

  /** Sursa activă. */
  get _src() {
    return this._sources.get(this.sourceKey);
  }

  /**
   * Înregistrează un store Zarr ca sursă de volume.
   *
   * Sursele diferă prin axa verticală: volumele CAMS au nivelurile direct în
   * metri deasupra solului, în timp ce cele meteo sunt pe nivele de presiune,
   * a căror altitudine reală vine din câmpul de înălțime geopotențială. Aici
   * normalizăm ambele la un profil de altitudini în metri, crescător.
   *
   * @param {string} key
   * @param {{url: string, altitudeVar?: string}} opts
   */
  async addSource(key, { url, altitudeVar = null }) {
    const store = new zarr.FetchStore(url);
    const root = await zarr.open(store, { kind: 'group' });

    const [latArr, lonArr, levArr, timeArr] = await Promise.all([
      zarr.open(root.resolve('latitude'), { kind: 'array' }),
      zarr.open(root.resolve('longitude'), { kind: 'array' }),
      zarr.open(root.resolve('level'), { kind: 'array' }),
      zarr.open(root.resolve('time'), { kind: 'array' }),
    ]);
    const [lats, lons, levels, times] = await Promise.all([
      zarr.get(latArr), zarr.get(lonArr), zarr.get(levArr), zarr.get(timeArr),
    ]);

    let altitudes = Array.from(levels.data, Number);
    if (altitudeVar) {
      // Profilul mediu de altitudine, luat din primul pas de timp: înălțimea
      // geopotențială a unui nivel de presiune variază sub 1% pe 48 de ore.
      const arr = await zarr.open(root.resolve(altitudeVar), { kind: 'array' });
      const view = await zarr.get(arr, [0, null, null, null]);
      const [nlev, nlat, nlon] = view.shape;
      const perLevel = nlat * nlon;
      altitudes = [];
      for (let l = 0; l < nlev; l++) {
        let sum = 0;
        const off = l * perLevel;
        for (let i = 0; i < perLevel; i++) sum += view.data[off + i];
        altitudes.push(sum / perLevel);
      }
    }

    const source = {
      root,
      altitudes,
      coords: {
        lats: Array.from(lats.data, Number),
        lons: Array.from(lons.data, Number),
        levels: Array.from(levels.data, Number),
        times: times.data,
        timeAttrs: timeArr.attrs || {},
      },
      arrays: {},
      cache: new Map(),
    };
    this._sources.set(key, source);
    this.available = true;
    return source;
  }

  /** Verifică dacă un câmp există într-o sursă. */
  async hasVariable(sourceKey, variable) {
    const src = this._sources.get(sourceKey);
    if (!src) return false;
    try {
      await zarr.open(src.root.resolve(variable), { kind: 'array' });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Alege câmpul afișat de felii.
   * @param {string} sourceKey
   * @param {string} variable
   * @param {(value:number)=>number[]} colorFn
   */
  setField(sourceKey, variable, colorFn) {
    this.sourceKey = sourceKey;
    this.variable = variable;
    this.colorFn = colorFn;
    this._lutKey = null;   // paleta se reconstruiește pentru noul câmp
  }

  /**
   * Aduce cubul (level, lat, lon) pentru un pas de timp. Store-ul e chunk-uit
   * pe timp, deci fiecare pas e exact un chunk.
   */
  async _cube(ti) {
    const src = this._src;
    const key = `${this.variable}:${ti}`;
    if (src.cache.has(key)) return src.cache.get(key);

    if (!src.arrays[this.variable]) {
      src.arrays[this.variable] = await zarr.open(
        src.root.resolve(this.variable), { kind: 'array' }
      );
    }
    const view = await zarr.get(src.arrays[this.variable], [ti, null, null, null]);
    const cube = { data: view.data, shape: view.shape };

    // Câteva pași de timp în memorie sunt suficienți pentru scrubbing fluent.
    if (src.cache.size > 8) src.cache.delete(src.cache.keys().next().value);
    src.cache.set(key, cube);
    return cube;
  }

  /**
   * Tabelă de culori pentru cubul curent.
   *
   * `colorFn` face interpolare la fiecare apel; la ~50.000 de pixeli pe felie,
   * asta domina costul redesenării. Cuantizăm o singură dată per câmp și pas.
   *
   * Domeniul se ia din date, nu de la zero: temperatura are valori negative,
   * deci o scară ancorată în 0 ar arunca jumătate din câmp în afara paletei.
   */
  _ensureLut(cube) {
    const key = `${this.sourceKey}:${this.variable}:${this.timeIndex}`;
    if (this._lutKey === key) return;

    let vmin = Infinity, vmax = -Infinity;
    const d = cube.data;
    for (let i = 0; i < d.length; i++) {
      const v = d[i];
      if (v < vmin) vmin = v;
      if (v > vmax) vmax = v;
    }
    if (!Number.isFinite(vmin) || !Number.isFinite(vmax) || vmax === vmin) {
      vmin = 0; vmax = 1;
    }

    const lut = new Uint8Array(LUT_SIZE * 4);
    for (let i = 0; i < LUT_SIZE; i++) {
      const value = vmin + (vmax - vmin) * i / (LUT_SIZE - 1);
      const [r, g, b, a] = this.colorFn(value);
      lut[i * 4] = r; lut[i * 4 + 1] = g; lut[i * 4 + 2] = b; lut[i * 4 + 3] = a;
    }
    this._lut = lut;
    this._lutMin = vmin;
    this._lutMax = vmax;
    this._lutKey = key;
  }

  /**
   * Construiește textura feliei: coloane = eșantioane pe traseu,
   * rânduri = altitudini echidistante (rândul 0 = vârf).
   */
  _buildTexture(cube, start, end, cols, rows) {
    const { lats, lons } = this._src.coords;
    const altitudes = this._src.altitudes;
    const [nlev, nlat, nlon] = cube.shape;
    const topM = altitudes[altitudes.length - 1];

    const canvas = document.createElement('canvas');
    canvas.width = cols;
    canvas.height = rows;
    const ctx = canvas.getContext('2d');
    const img = ctx.createImageData(cols, rows);
    const px = img.data;

    const latStep = (lats[lats.length - 1] - lats[0]) / (nlat - 1);
    const lonStep = (lons[lons.length - 1] - lons[0]) / (nlon - 1);

    const lut = this._lut;
    const lutScale = (LUT_SIZE - 1) / (this._lutMax - this._lutMin);
    const lutMin = this._lutMin;

    // Coloană de valori pe niveluri, refolosită între rânduri.
    const column = new Float64Array(nlev);

    for (let x = 0; x < cols; x++) {
      const t = cols === 1 ? 0 : x / (cols - 1);
      const lat = start.lat + (end.lat - start.lat) * t;
      const lon = start.lon + (end.lon - start.lon) * t;

      const fy = (lat - lats[0]) / latStep;
      const fx = (lon - lons[0]) / lonStep;

      for (let l = 0; l < nlev; l++) {
        column[l] = bilinear(cube.data, nlat, nlon, l * nlat * nlon, fy, fx);
      }

      for (let y = 0; y < rows; y++) {
        // rândul 0 e vârful texturii → altitudinea maximă
        const alt = topM * (1 - y / (rows - 1));

        // interpolare liniară în altitudine între nivelurile care încadrează
        let li = 0;
        while (li < nlev - 2 && altitudes[li + 1] < alt) li++;
        const a0 = altitudes[li], a1 = altitudes[li + 1];
        const f = a1 === a0 ? 0 : (alt - a0) / (a1 - a0);
        const value = column[li] + (column[li + 1] - column[li]) * f;

        let idx = ((value - lutMin) * lutScale) | 0;
        if (idx < 0) idx = 0; else if (idx >= LUT_SIZE) idx = LUT_SIZE - 1;
        const o = (y * cols + x) * 4;
        const s = idx * 4;
        px[o] = lut[s]; px[o + 1] = lut[s + 1];
        px[o + 2] = lut[s + 2]; px[o + 3] = lut[s + 3] * this.opacity;
      }
    }

    ctx.putImageData(img, 0, 0);
    return canvas;
  }

  // ── Teren ───────────────────────────────────────────────────────
  /** Punctele de pe traseul feliei, ca Cartographic. */
  _pathCartographics(def) {
    const out = [];
    for (let i = 0; i < SAMPLES; i++) {
      const t = i / (SAMPLES - 1);
      out.push(Cartographic.fromDegrees(
        def.start.lon + (def.end.lon - def.start.lon) * t,
        def.start.lat + (def.end.lat - def.start.lat) * t,
      ));
    }
    return out;
  }

  /**
   * Profilul de teren sub felie.
   *
   * `sampleTerrainMostDetailed` costă peste 100 ms — de zece ori mai mult
   * decât desenarea texturii — deci nu poate sta pe calea critică a tragerii.
   * În regim rapid folosim ultimul profil cunoscut și cerem unul nou în
   * fundal; decalajul de un cadru sau două nu se vede.
   */
  /** Identifică traseul, ca să știm dacă profilul din cache mai e valabil. */
  static _pathKey(def) {
    return `${def.start.lon},${def.start.lat},${def.end.lon},${def.end.lat}`;
  }

  async _sampleGround(def) {
    const key = VolumeSlicer._pathKey(def);
    try {
      const sampled = await sampleTerrainMostDetailed(
        this.viewer.terrainProvider, this._pathCartographics(def)
      );
      def.ground = sampled.map(c => (Number.isFinite(c.height) ? c.height : 0));
      def.groundKey = key;
    } catch {
      def.ground = def.ground || new Array(SAMPLES).fill(0);
    }
    return def.ground;
  }

  _sampleGroundAsync(def) {
    if (def._groundPending) return;
    def._groundPending = true;
    this._sampleGround(def).finally(() => { def._groundPending = false; });
  }

  // ── Felii ───────────────────────────────────────────────────────
  /**
   * Adaugă o felie verticală între două puncte.
   * @param {{lon:number, lat:number}} start
   * @param {{lon:number, lat:number}} end
   */
  async addCurtain(start, end) {
    if (!this.available) return null;
    const def = { start, end, ground: null };
    this._curtains.push(def);
    await this._render(def);
    this._requestRenders();
    return def;
  }

  /**
   * Așază două felii perpendiculare prin punctul dat, pe toată întinderea
   * domeniului. Apelul repetat mută crucea — asta e ce se întâmplă la tragere.
   *
   * @param {number} lon
   * @param {number} lat
   * @param {{fast?: boolean}} [opts] regim rapid, pentru tragere
   */
  async setCross(lon, lat, { fast = false } = {}) {
    if (!this.available) return;
    const b = this.bounds;
    const clampedLon = Math.min(Math.max(lon, b.west), b.east);
    const clampedLat = Math.min(Math.max(lat, b.south), b.north);
    this._cross = { lon: clampedLon, lat: clampedLat };

    const wantEW = { start: { lon: b.west, lat: clampedLat }, end: { lon: b.east, lat: clampedLat } };
    const wantNS = { start: { lon: clampedLon, lat: b.south }, end: { lon: clampedLon, lat: b.north } };

    if (this._curtains.length === 2) {
      // Păstrăm profilele de teren deja calculate ca punct de plecare.
      this._curtains[0].start = wantEW.start; this._curtains[0].end = wantEW.end;
      this._curtains[1].start = wantNS.start; this._curtains[1].end = wantNS.end;
    } else {
      this._curtains = [
        { ...wantEW, ground: null },
        { ...wantNS, ground: null },
      ];
    }

    this._clearEntities();
    for (const def of this._curtains) await this._render(def, { fast });
    this._updateHandle();
    this._requestRenders(fast ? 2 : 8);
  }

  get crossCenter() {
    return this._cross;
  }

  // ── Felia orizontală (axa Z) ────────────────────────────────────
  /**
   * Așază o felie orizontală la altitudinea dată, pe tot domeniul.
   * @param {number|null} altM  metri deasupra solului; null o ascunde
   */
  async setLevel(altM, { fast = false } = {}) {
    if (!this.available) return;
    if (altM === null) return this.hideLevel();

    this._levelAlt = altM;
    const cube = await this._cube(this.timeIndex);
    this._ensureLut(cube);

    const canvas = this._buildLevelTexture(cube, altM, fast);
    const b = this.bounds;

    if (this._levelEntity) this.viewer.entities.remove(this._levelEntity);
    this._levelEntity = this.viewer.entities.add({
      rectangle: {
        coordinates: Rectangle.fromDegrees(b.west, b.south, b.east, b.north),
        height: altM * this.exaggeration,
        material: new ImageMaterialProperty({ image: canvas, transparent: true }),
      },
    });
    this._requestRenders(fast ? 2 : 8);
  }

  hideLevel() {
    this._levelAlt = null;
    if (this._levelEntity) {
      this.viewer.entities.remove(this._levelEntity);
      this._levelEntity = null;
    }
    this._requestRenders(2);
  }

  get levelAlt() {
    return this._levelAlt;
  }

  /**
   * Harta poluantului pe un plan orizontal.
   *
   * Grila e mică (58×116), deci desenăm la rezoluția ei și lăsăm canvas-ul să
   * interpoleze la mărire — la fel ca rasterul de suprafață.
   */
  _buildLevelTexture(cube, altM, fast) {
    const altitudes = this._src.altitudes;
    const [nlev, nlat, nlon] = cube.shape;

    let l = 0;
    while (l < nlev - 2 && altitudes[l + 1] < altM) l++;
    const a0 = altitudes[l], a1 = altitudes[l + 1];
    const f = a1 === a0 ? 0 : Math.min(Math.max((altM - a0) / (a1 - a0), 0), 1);
    const o0 = l * nlat * nlon;
    const o1 = (l + 1) * nlat * nlon;

    const lut = this._lut;
    const lutScale = (LUT_SIZE - 1) / (this._lutMax - this._lutMin);
    const lutMin = this._lutMin;

    const base = document.createElement('canvas');
    base.width = nlon;
    base.height = nlat;
    const bctx = base.getContext('2d');
    const img = bctx.createImageData(nlon, nlat);
    const px = img.data;

    for (let y = 0; y < nlat; y++) {
      // latitudinile cresc spre nord, canvas-ul crește în jos
      const row = (nlat - 1 - y) * nlon;
      for (let x = 0; x < nlon; x++) {
        const v0 = cube.data[o0 + row + x];
        const v1 = cube.data[o1 + row + x];
        const value = v0 + (v1 - v0) * f;

        let idx = ((value - lutMin) * lutScale) | 0;
        if (idx < 0) idx = 0; else if (idx >= LUT_SIZE) idx = LUT_SIZE - 1;
        const o = (y * nlon + x) * 4;
        const s = idx * 4;
        px[o] = lut[s]; px[o + 1] = lut[s + 1];
        px[o + 2] = lut[s + 2]; px[o + 3] = lut[s + 3] * this.opacity;
      }
    }
    bctx.putImageData(img, 0, 0);

    if (fast) return base;

    const scale = 4;
    const up = document.createElement('canvas');
    up.width = nlon * scale;
    up.height = nlat * scale;
    const uctx = up.getContext('2d');
    uctx.imageSmoothingEnabled = true;
    uctx.imageSmoothingQuality = 'high';
    uctx.drawImage(base, 0, 0, up.width, up.height);
    return up;
  }

  get handle() {
    return this._handle;
  }

  /** Reconstruiește toate feliile (după schimbarea variabilei/timpului/exagerării). */
  async refresh() {
    if (!this.available) return;
    if (this._curtains.length > 0) {
      this._clearEntities();
      for (const def of this._curtains) await this._render(def);
      this._updateHandle();
    }
    if (this._levelAlt !== null) await this.setLevel(this._levelAlt);
    this._requestRenders();
  }

  async _render(def, { fast = false } = {}) {
    const cube = await this._cube(this.timeIndex);
    this._ensureLut(cube);

    // Feliile își schimbă traseul la tragere, deci profilul din cache poate
    // fi al poziției anterioare. În regim rapid îl folosim oricum și cerem
    // unul nou în fundal; la comitere îl așteptăm.
    const stale = def.groundKey !== VolumeSlicer._pathKey(def);
    if (stale) {
      if (fast) this._sampleGroundAsync(def);
      else await this._sampleGround(def);
    }
    const ground = def.ground || new Array(SAMPLES).fill(0);

    const canvas = this._buildTexture(
      cube, def.start, def.end,
      fast ? TEX_COLS_FAST : TEX_COLS,
      fast ? TEX_ROWS_FAST : TEX_ROWS,
    );

    const altitudes = this._src.altitudes;
    const topM = altitudes[altitudes.length - 1];
    const ex = this.exaggeration;

    const positions = [];
    const minimumHeights = [];
    const maximumHeights = [];
    const lonlat = [];
    for (let i = 0; i < SAMPLES; i++) {
      const t = i / (SAMPLES - 1);
      const lon = def.start.lon + (def.end.lon - def.start.lon) * t;
      const lat = def.start.lat + (def.end.lat - def.start.lat) * t;
      const base = (ground[i] || 0) * ex;
      lonlat.push([lon, lat]);
      positions.push(lon, lat, base + topM * ex);
      minimumHeights.push(base);
      maximumHeights.push(base + topM * ex);
    }

    const wall = this.viewer.entities.add({
      wall: {
        positions: Cartesian3.fromDegreesArrayHeights(positions),
        minimumHeights,
        maximumHeights,
        material: new ImageMaterialProperty({
          image: canvas,
          transparent: true,
        }),
      },
    });
    this._entities.push(wall);

    this._addFrame(lonlat, minimumHeights, maximumHeights);
    // Etichetele sunt entități separate; la tragere ar însemna 18 obiecte
    // recreate pe cadru, fără să aducă informație în plus.
    if (!fast) this._addAxis(def, ground[0] || 0, ex);
  }

  /** Muchia superioară și capetele — dau feliei contur, altfel se pierde în fundal. */
  _addFrame(lonlat, minH, maxH) {
    const edge = Color.fromCssColorString('#e2e8f0').withAlpha(0.35);

    const top = [];
    for (let i = 0; i < lonlat.length; i++) {
      top.push(lonlat[i][0], lonlat[i][1], maxH[i]);
    }
    const lines = [top];

    for (const i of [0, lonlat.length - 1]) {
      lines.push([
        lonlat[i][0], lonlat[i][1], minH[i],
        lonlat[i][0], lonlat[i][1], maxH[i],
      ]);
    }

    for (const coords of lines) {
      this._entities.push(this.viewer.entities.add({
        polyline: {
          positions: Cartesian3.fromDegreesArrayHeights(coords),
          width: 1.5,
          material: edge,
          arcType: ArcType.NONE,
        },
      }));
    }
  }

  /** Etichete de altitudine la capătul feliei, ca reper vertical. */
  _addAxis(def, groundStart, ex) {
    const altitudes = this._src.altitudes;
    for (const alt of altitudes) {
      if (alt === 0) continue;
      const rounded = Math.round(alt);
      const label = this.viewer.entities.add({
        position: Cartesian3.fromDegrees(
          def.start.lon, def.start.lat, (groundStart + alt) * ex
        ),
        label: {
          text: rounded >= 1000 ? `${(rounded / 1000).toFixed(1)} km` : `${rounded} m`,
          font: '11px JetBrains Mono, monospace',
          fillColor: Color.fromCssColorString('#cbd5e1'),
          outlineColor: Color.fromCssColorString('#0a0e1a'),
          outlineWidth: 3,
          style: LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: VerticalOrigin.CENTER,
          horizontalOrigin: HorizontalOrigin.RIGHT,
          pixelOffset: new Cartesian2(-6, 0),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });
      this._entities.push(label);
    }
  }

  /**
   * Mânerul de la intersecția crucii.
   *
   * E o entitate persistentă, nu una recreată la fiecare cadru: altfel
   * `scene.pick` ar pierde obiectul chiar în timpul tragerii.
   */
  _updateHandle() {
    if (!this._cross) return;
    const altitudes = this._src.altitudes;
    const topM = altitudes[altitudes.length - 1];
    const def = this._curtains[0];
    const ground = def?.ground?.[Math.floor(SAMPLES / 2)] || 0;
    const height = (ground + topM) * this.exaggeration;

    const position = Cartesian3.fromDegrees(this._cross.lon, this._cross.lat, height);

    if (!this._handle) {
      this._handle = this.viewer.entities.add({
        position,
        point: {
          pixelSize: 14,
          color: Color.fromCssColorString('#00d2ff').withAlpha(0.9),
          outlineColor: Color.WHITE,
          outlineWidth: 2,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        label: {
          text: 'trage',
          font: '10px Inter, sans-serif',
          fillColor: Color.fromCssColorString('#e2e8f0'),
          outlineColor: Color.fromCssColorString('#0a0e1a'),
          outlineWidth: 3,
          style: LabelStyle.FILL_AND_OUTLINE,
          verticalOrigin: VerticalOrigin.BOTTOM,
          pixelOffset: new Cartesian2(0, -14),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
          translucencyByDistance: new NearFarScalar(3.0e5, 1.0, 3.0e6, 0.0),
        },
      });
    } else {
      this._handle.position = position;
    }
  }

  _clearEntities() {
    this._entities.forEach(e => this.viewer.entities.remove(e));
    this._entities = [];
  }

  /**
   * Cere mai multe cadre consecutive.
   *
   * Cu `requestRenderMode` activ, textura perdelei ajunge pe GPU abia într-un
   * cadru ulterior celui în care am cerut randarea. Un singur `requestRender`
   * lasă peretele desenat cu materialul implicit — adică alb.
   */
  _requestRenders(frames = 8) {
    let remaining = frames;
    const tick = () => {
      this.viewer.scene.requestRender();
      if (--remaining > 0) requestAnimationFrame(tick);
    };
    requestAnimationFrame(tick);
  }

  /** Șterge toate feliile. */
  clear() {
    this._clearEntities();
    if (this._handle) {
      this.viewer.entities.remove(this._handle);
      this._handle = null;
    }
    this.hideLevel();
    this._curtains = [];
    this._cross = null;
    this._requestRenders(2);
  }

  // ── Acces la volumul brut ───────────────────────────────────────
  // Slicer-ul e singurul care ține deschise store-urile și normalizează axa
  // verticală, așa că expune volumul și altor reprezentări (nor volumetric).

  /** Cubul (level, lat, lon) pentru un pas de timp din sursa activă. */
  async cubeFor(ti) {
    return this._cube(ti);
  }

  /** Altitudinile în metri ale nivelurilor sursei active, crescător. */
  get altitudes() {
    return this._src?.altitudes ?? null;
  }

  /** Coordonatele orizontale ale sursei active. */
  get coords() {
    return this._src?.coords ?? null;
  }

  /** Domeniul de valori al câmpului curent, așa cum a fost cuantizat în paletă. */
  get valueRange() {
    if (!this._lut) return null;
    return { min: this._lutMin, max: this._lutMax };
  }

  /** Altitudinea maximă a volumului, în metri. */
  get topAltitude() {
    const altitudes = this._src.altitudes;
    return altitudes[altitudes.length - 1];
  }

  get count() {
    return this._curtains.length;
  }

  /** Limitele geografice ale volumului. */
  get bounds() {
    const { lats, lons } = this._src.coords;
    return {
      west: lons[0], east: lons[lons.length - 1],
      south: lats[0], north: lats[lats.length - 1],
    };
  }

  /** Numărul de pași de timp din volum (3-orari, deci mai puțini decât rasterul). */
  get timeSteps() {
    return this._src.coords.times.length;
  }
}
